import './FullScanStatusBanner.scss';
import { useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { Spinner } from '@Components/Spinner/Spinner';
import {
  FullScanJobStatus,
  FullScanStatus,
  hasUsableFullScanResult,
  TERMINAL_FULL_SCAN_STATUSES
} from '../full-scan/full-scan-constants';

interface FullScanStatusBannerProps {
  job: FullScanJobStatus | undefined;
  merged: boolean;
  // True when the merged job stopped at FullScanStatus.PARTIAL - it still
  // carries real per-page evidence for whatever it captured (see
  // DigitalAudit.tsx's merge effect), so this only changes the "merged"
  // message's wording, not whether merged/isUnmergeable are true.
  partial?: boolean;
  error: Error | null;
  // True once polling has given up on a job that's stuck at a non-terminal
  // status (see useFullScanJobPolling's MAX_POLL_MS) - distinct from
  // `error`/FAILED, since the job itself never reported a failure.
  stalled?: boolean;
  // DEV-24211: resumes polling after a stall - the job may complete
  // server-side after the client gave up on it. Only meaningful when
  // `stalled` is true; other unmergeable reasons (FAILED, a genuinely empty
  // result) won't produce a different outcome on retry.
  onRetry?: () => void;
}

export const FullScanStatusBanner = ({
  job,
  merged,
  partial = false,
  error,
  stalled = false,
  onRetry
}: FullScanStatusBannerProps) => {
  const [dismissed, setDismissed] = useState(false);

  // DEV-24211: a job can reach a terminal status (DONE/PARTIAL) and still
  // carry no usable result (e.g. the crawl's result upload failed) - without
  // this, the merge effect would just skip it and this banner would fall
  // through to the "running" branch forever, since polling has already
  // stopped for a terminal job.
  const isTerminalButUnusable =
    Boolean(job?.status && TERMINAL_FULL_SCAN_STATUSES.includes(job.status)) &&
    !hasUsableFullScanResult(job);
  const isUnmergeable =
    Boolean(error) || stalled || job?.status === FullScanStatus.FAILED || isTerminalButUnusable;

  // Re-show the banner on a new scan (new job id) AND whenever the same
  // job's outcome changes category (running -> unmergeable/merged) - a user
  // who dismissed the "running" banner should still see the later
  // done/failed outcome instead of it staying silently dismissed.
  let outcome = 'running';
  if (isUnmergeable) {
    outcome = 'unmergeable';
  } else if (merged) {
    outcome = 'merged';
  }
  const bannerKey = `${job?.id ?? 'none'}:${outcome}`;
  useEffect(() => {
    setDismissed(false);
  }, [bannerKey]);

  if (dismissed) return null;

  if (isUnmergeable) {
    return (
      <Alert
        variant='warning'
        dismissible
        onClose={() => setDismissed(true)}
        className='full-scan-status-banner'
      >
        <span>
          {stalled
            ? 'Full scan is taking longer than expected — still showing the quick-scan preview below.'
            : `Full scan couldn't complete${error ? '' : ' for every page'} — still showing the quick-scan preview below.`}
        </span>
        {stalled && onRetry && (
          <Button size={Size.LG} appearance={Appearance.OUTLINED} onClick={onRetry}>
            Keep waiting
          </Button>
        )}
      </Alert>
    );
  }

  if (merged) {
    return (
      <Alert
        variant='success'
        dismissible
        onClose={() => setDismissed(true)}
        className='full-scan-status-banner'
      >
        {partial
          ? "Full scan finished but couldn't capture every page — results below reflect a full-browser crawl of the pages it did reach, not just the quick-scan preview."
          : 'Full scan complete — results below reflect a full-browser crawl, not just the quick-scan preview.'}
      </Alert>
    );
  }

  // DEV-24211: `progress` is typed required but never runtime-validated
  // (awaitJSON is an unchecked cast) - chain past it too, not just `job`.
  const pagesCompleted = job?.progress?.pagesCompleted ?? 0;
  const pagesTotal = job?.progress?.pagesTotal ?? 0;

  return (
    <Alert variant='info' className='full-scan-status-banner'>
      <Spinner size={20} />
      <span>
        Showing a quick-scan preview. Running a full browser scan in the background
        {pagesTotal > 0 ? ` (${pagesCompleted} of ${pagesTotal} pages)` : ''}…
      </span>
    </Alert>
  );
};
